import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useAiakoTextChat, TextChatState } from './useAiakoTextChat';
import { MessageBubble } from './AiakoComponents';
import AiakoSummary from './AiakoSummary';

interface AiakoChatCallProps {
  onBack: () => void;
}

const TypingIndicator = () => (
  <div className="flex items-start pr-8">
    <div className="flex gap-1.5 items-center rounded-bl-[2px] rounded-br-2xl rounded-tl-2xl rounded-tr-2xl bg-white px-4 py-3">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="h-2 w-2 rounded-full"
          style={{
            backgroundColor: '#ADB1B4',
            animation: 'bounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
      <style>{`@keyframes bounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-6px); } }`}</style>
    </div>
  </div>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface InputBarProps {
  inputText: string;
  isChatActive: boolean;
  mobile?: boolean;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
}

const InputBar: React.FC<InputBarProps> = ({ inputText, isChatActive, mobile = false, inputRef, onChange, onKeyDown, onSend }) => (
  <div className={`flex items-end gap-2 ${mobile ? 'px-4 py-3' : 'p-4'}`}>
    <textarea
      ref={inputRef}
      value={inputText}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder={isChatActive ? 'Type your message…' : ''}
      disabled={!isChatActive}
      rows={1}
      className="flex-1 resize-none rounded-xl border px-4 py-2.5 text-sm leading-5 outline-none disabled:opacity-50"
      style={{
        borderColor: '#D9DDE0',
        color: '#161618',
        fontWeight: 450,
        letterSpacing: '-0.084px',
        maxHeight: '120px',
        overflowY: 'auto',
      }}
    />
    <button
      onClick={onSend}
      disabled={!isChatActive || !inputText.trim()}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full disabled:opacity-40"
      style={{ backgroundColor: '#CC0044' }}
    >
      <SendIcon />
    </button>
  </div>
);

const AiakoChatCall: React.FC<AiakoChatCallProps> = ({ onBack }) => {
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const desktopScrollAnchorRef = useRef<HTMLDivElement>(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [finalDuration, setFinalDuration] = useState('0:00');
  const [inputText, setInputText] = useState('');
  const wasActiveRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    chatState,
    error,
    transcript,
    isChatActive,
    isAgentTyping,
    chatDuration,
    startChat,
    sendMessage,
    endChat,
    formatDuration,
  } = useAiakoTextChat();

  const isConnecting = chatState === TextChatState.CONNECTING;

  useEffect(() => {
    if (isChatActive) {
      wasActiveRef.current = true;
    } else if (wasActiveRef.current && chatState === TextChatState.INITIAL) {
      setFinalDuration(formatDuration(chatDuration));
      setShowSummary(true);
      wasActiveRef.current = false;
    }
  }, [isChatActive, chatState, chatDuration, formatDuration]);

  const scrollToBottom = useCallback(() => {
    scrollAnchorRef.current?.scrollIntoView({ block: 'end', behavior: 'instant' as ScrollBehavior });
    desktopScrollAnchorRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [transcript, isAgentTyping, scrollToBottom]);

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text || !isChatActive) return;
    sendMessage(text);
    setInputText('');
    inputRef.current?.focus();
  }, [inputText, isChatActive, sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  }, []);

  const handleEndConfirm = () => {
    setFinalDuration(formatDuration(chatDuration));
    endChat();
    setShowEndModal(false);
  };

  const getStatusText = () => {
    if (isConnecting) return 'Connecting...';
    if (isChatActive) return 'Connected to Aiako';
    return 'Ready to connect';
  };
  const getStatusBg = () => isConnecting ? '#FFF4E6' : isChatActive ? '#E7F8F3' : '#EAEDEF';
  const getStatusColor = () => isConnecting ? '#C77700' : isChatActive ? '#058A62' : '#58595A';

  if (showSummary) {
    return (
      <AiakoSummary
        transcript={transcript}
        duration={finalDuration}
        onAskAgain={() => setShowSummary(false)}
      />
    );
  }

  return (
    <div className="relative min-h-screen" style={{ backgroundColor: '#F6F8F8' }}>

      {/* Mobile */}
      <div className="md:hidden flex flex-col fixed inset-x-0 top-0" style={{ height: '100dvh', backgroundColor: '#F6F8F8' }}>
        <div className="bg-white flex flex-col items-center pb-2 pt-6 px-4 shrink-0">
          <div className="flex items-center justify-between w-full py-2">
            <button onClick={onBack} className="w-6 h-6 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M10 8L6 12M6 12L10 16M6 12H18" stroke="#58595A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h1 className="text-base font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.16px' }}>Ask Aiako</h1>
            {isChatActive || isConnecting ? (
              <button onClick={() => setShowEndModal(true)} disabled={isConnecting}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full shrink-0 disabled:opacity-50"
                style={{ backgroundColor: '#E60D00' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            ) : (
              <div className="w-10 h-10" />
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0 }}>
          <div className="flex flex-col gap-3">
            {!isChatActive && !isConnecting && (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                {error && <p className="text-sm text-center px-6" style={{ color: '#E60D00' }}>{error}</p>}
                <button onClick={startChat} className="h-10 px-6 rounded-full flex items-center gap-2" style={{ backgroundColor: '#CC0044' }}>
                  <span className="text-sm text-white" style={{ fontWeight: 450 }}>Start chat</span>
                </button>
              </div>
            )}
            {transcript.map((entry, i) => <MessageBubble key={i} entry={entry} />)}
            {isAgentTyping && <TypingIndicator />}
            <div ref={scrollAnchorRef} className="h-px" />
          </div>
        </div>

        <div className="bg-white rounded-t-3xl shrink-0" style={{ boxShadow: '0px -2px 12px 0px rgba(26,24,23,0.08)' }}>
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <div className="flex items-center justify-center rounded-full px-2 py-0.5" style={{ backgroundColor: getStatusBg() }}>
              <p className="text-xs leading-4" style={{ color: getStatusColor(), fontWeight: 450 }}>{getStatusText()}</p>
            </div>
            <p className="text-lg font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.18px' }}>{formatDuration(chatDuration)}</p>
          </div>
          <InputBar
            inputText={inputText}
            isChatActive={isChatActive}
            mobile
            inputRef={inputRef}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onSend={handleSend}
          />
          <p className="text-[10px] leading-[14px] text-center pb-3" style={{ color: '#7E7F81', fontWeight: 450 }}>
            Aiako provides general information only. Please verify important details.
          </p>
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:flex flex-col min-h-screen">
        <div className="w-full p-2">
          <div className="w-full max-w-[1200px] mx-auto p-2 flex justify-between items-center">
            <button onClick={onBack} className="flex items-center gap-1.5 text-sm" style={{ color: '#58595A' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M10 8L6 12M6 12L10 16M6 12H18" stroke="#58595A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back
            </button>
            <span className="text-xl font-bold leading-7" style={{ color: '#161618', letterSpacing: '-0.3px' }}>Aiako</span>
            <div className="w-16" />
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center py-10 px-4">
          <div className="flex justify-center w-full" style={{ maxWidth: '1000px', height: '576px', boxShadow: '0px 9px 20px rgba(209,209,209,0.10), 0px 37px 37px rgba(209,209,209,0.09), 0px 82px 49px rgba(209,209,209,0.05), 0px 147px 59px rgba(209,209,209,0.01)', borderRadius: '24px' }}>

            {/* Left panel */}
            <div className="flex w-[360px] shrink-0 flex-col items-center justify-center gap-6 rounded-l-3xl bg-white px-16 py-12">
              <div className="relative h-36 w-36 shrink-0">
                <div className="absolute left-3 top-3 w-[120px] h-[120px] rounded-full overflow-hidden" style={{ background: '#770032' }}>
                  <img src="/davy-5665cdbf.png" alt="Aiako" className="w-full h-full object-cover" />
                </div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center justify-center rounded-full px-2 py-0.5" style={{ backgroundColor: getStatusBg() }}>
                  <p className="text-xs leading-4" style={{ color: getStatusColor(), fontWeight: 450 }}>{getStatusText()}</p>
                </div>
                <p className="text-xl font-semibold leading-7 text-center" style={{ color: '#161618', letterSpacing: '-0.3px' }}>{formatDuration(chatDuration)}</p>
              </div>

              {!isChatActive && !isConnecting ? (
                <button onClick={startChat} className="h-10 px-6 rounded-full flex items-center gap-2 cursor-pointer" style={{ backgroundColor: '#CC0044' }}>
                  <span className="text-sm text-white" style={{ fontWeight: 450 }}>Start chat</span>
                </button>
              ) : isConnecting ? (
                <button disabled className="h-10 px-6 rounded-full flex items-center gap-2 cursor-not-allowed" style={{ backgroundColor: '#C77700' }}>
                  <span className="text-sm text-white" style={{ fontWeight: 450 }}>Connecting...</span>
                </button>
              ) : (
                <button onClick={() => setShowEndModal(true)} className="h-10 px-6 rounded-full flex items-center gap-2 cursor-pointer" style={{ backgroundColor: '#E60D00' }}>
                  <span className="text-sm text-white" style={{ fontWeight: 450 }}>End chat</span>
                </button>
              )}

              {error && <p className="text-xs text-center" style={{ color: '#E60D00' }}>{error}</p>}
            </div>

            {/* Right panel */}
            <div className="relative flex-1 h-full flex flex-col rounded-r-3xl" style={{ backgroundColor: '#F6F8F8' }}>
              <div className="flex-1 overflow-y-auto py-6 pl-6 pr-8" style={{ minHeight: 0 }}>
                <div className="flex flex-col gap-4">
                  {transcript.length === 0 && isChatActive && (
                    <div className="flex items-center justify-center h-32">
                      <p className="text-sm" style={{ color: '#ADB1B4', fontWeight: 450 }}>Type a message to start the conversation…</p>
                    </div>
                  )}
                  {transcript.map((entry, i) => <MessageBubble key={i} entry={entry} />)}
                  {isAgentTyping && <TypingIndicator />}
                  <div ref={desktopScrollAnchorRef} className="h-px" />
                </div>
              </div>

              {isChatActive && (
                <div className="border-t shrink-0" style={{ borderColor: '#E8EBED', backgroundColor: '#F6F8F8' }}>
                  <InputBar
                    inputText={inputText}
                    isChatActive={isChatActive}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onSend={handleSend}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm leading-5" style={{ color: '#58595A', fontWeight: 450, letterSpacing: '-0.084px' }}>
              Aiako provides general information only. Please verify important details.
            </p>
          </div>
          <div className="mt-4 flex items-center justify-center gap-1.5">
            <span className="text-xs leading-4" style={{ color: '#7E7F81', fontWeight: 450 }}>Powered by</span>
            <img src="/logos/Hupo_Logotype_Orange(noR).svg" alt="Hupo" className="h-3.5" />
          </div>
        </div>
      </div>

      {/* End chat modal */}
      {showEndModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setShowEndModal(false)}>
          <div className="flex w-full max-w-[400px] flex-col gap-2 rounded-2xl bg-white px-4 py-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8.333" stroke="#E60D00" strokeWidth="1.25" />
                <path d="M10 6.667v3.333" stroke="#E60D00" strokeWidth="1.25" strokeLinecap="round" />
                <circle cx="10" cy="13.333" r="0.417" fill="#E60D00" stroke="#E60D00" strokeWidth="0.833" />
              </svg>
              <p className="text-base font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.16px' }}>End chat with Aiako?</p>
            </div>
            <p className="text-base leading-6" style={{ color: '#58595A', fontWeight: 450, letterSpacing: '-0.16px' }}>
              Aiako won't be able to continue this conversation, but you can always start a new one.
            </p>
            <div className="flex items-end justify-end gap-2 pt-4">
              <button onClick={() => setShowEndModal(false)} className="flex h-10 items-center justify-center rounded-full px-6" style={{ backgroundColor: '#F9EFF2' }}>
                <p className="text-sm leading-5" style={{ color: '#CC0044', fontWeight: 450, letterSpacing: '-0.084px' }}>Continue chat</p>
              </button>
              <button onClick={handleEndConfirm} className="flex h-10 items-center justify-center rounded-full px-6" style={{ backgroundColor: '#CC0044' }}>
                <p className="text-sm leading-5 text-white" style={{ fontWeight: 450, letterSpacing: '-0.084px' }}>End anyway</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiakoChatCall;
